import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';

const GroceryListPanel = ({ items, onUpdateItems, familyMembers }) => {
  const [newItem, setNewItem] = useState('');
  const [filterMember, setFilterMember] = useState(null);
  const [showCompleted, setShowCompleted] = useState(true);

  const handleToggleItem = (itemId) => {
    const updatedItems = items?.map(item => 
      item?.id === itemId ? { ...item, checked: !item?.checked } : item
    );
    onUpdateItems(updatedItems);
  };

  const handleAddItem = () => {
    if (!newItem?.trim()) return;

    const newGroceryItem = {
      id: Date.now(),
      text: newItem?.trim(),
      checked: false,
      addedBy: 1,
      addedAt: new Date()?.toISOString()
    };

    onUpdateItems([...(items || []), newGroceryItem]);
    setNewItem('');
  };

  const handleDeleteItem = (itemId) => {
    const updatedItems = items?.filter(item => item?.id !== itemId);
    onUpdateItems(updatedItems);
  };

  const handleClearCompleted = () => {
    const updatedItems = items?.filter(item => !item?.checked);
    onUpdateItems(updatedItems);
  };

  const getMember = (memberId) => {
    return familyMembers?.find(m => m?.id === memberId);
  };

  const filteredItems = items?.filter(item => {
    if (filterMember && item?.addedBy !== filterMember) return false;
    if (!showCompleted && item?.checked) return false;
    return true;
  });

  const checkedCount = items?.filter(item => item?.checked)?.length || 0;
  const totalCount = items?.length || 0;
  const pendingCount = totalCount - checkedCount;

  return (
    <div className="flex flex-col h-[calc(100vh-400px)]">
      {/* Header with stats and filters */}
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-semibold text-foreground mb-1">Shopping List</h2>
            <p className="text-sm text-muted-foreground">
              {pendingCount} item{pendingCount !== 1 ? 's' : ''} to buy
            </p>
          </div>
          <div className="flex gap-2">
            {checkedCount > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleClearCompleted}
                iconName="trash-2"
              >
                Clear Completed
              </Button>
            )}
          </div>
        </div>

        {/* Progress bar */}
        {totalCount > 0 && (
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-foreground">
                {checkedCount} of {totalCount} completed
              </span>
              <span className="text-sm text-muted-foreground">
                {Math.round((checkedCount / totalCount) * 100)}%
              </span>
            </div>
            <div className="w-full bg-muted rounded-full h-2">
              <div
                className="bg-primary h-2 rounded-full transition-all duration-300"
                style={{ width: `${(checkedCount / totalCount) * 100}%` }}
              />
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Filter by:</span>
            <button
              onClick={() => setFilterMember(null)}
              className={`px-3 py-1 text-xs rounded-full transition-colors ${
                filterMember === null
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground hover:bg-muted/80'
              }`}
            >
              All
            </button>
            {familyMembers?.map(member => (
              <button
                key={member?.id}
                onClick={() => setFilterMember(member?.id)}
                className={`px-3 py-1 text-xs rounded-full transition-colors flex items-center gap-1 ${
                  filterMember === member?.id
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground hover:bg-muted/80'
                }`}
              >
                <div
                  className="w-2 h-2 rounded-full"
                  style={{ backgroundColor: member?.color }}
                />
                {member?.name}
              </button>
            ))}
          </div>
          <div className="ml-auto">
            <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer">
              <Checkbox
                checked={showCompleted}
                onChange={() => setShowCompleted(!showCompleted)}
              />
              Show completed
            </label>
          </div>
        </div>
      </div>

      {/* Grocery items list */}
      <div className="flex-1 overflow-y-auto p-6">
        {filteredItems?.length === 0 ? (
          <div className="flex items-center justify-center h-full text-center">
            <div>
              <Icon name="shopping-cart" size={48} className="mx-auto mb-4 text-muted-foreground" />
              <p className="text-muted-foreground">
                {items?.length === 0
                  ? 'No items yet. Add your first grocery item below!' :'No items match your filters.'}
              </p>
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            {filteredItems?.map((item) => {
              const member = getMember(item?.addedBy);
              return (
                <div
                  key={item?.id}
                  className="flex items-center gap-3 p-4 bg-card border border-border rounded-lg hover:bg-muted/50 transition-colors group"
                >
                  <Checkbox
                    checked={item?.checked}
                    onChange={() => handleToggleItem(item?.id)}
                    className="flex-shrink-0"
                  />
                  
                  <div className="flex-1 min-w-0">
                    <p
                      className={`text-base ${
                        item?.checked
                          ? 'line-through text-muted-foreground'
                          : 'text-foreground font-medium'
                      }`}
                    >
                      {item?.text}
                    </p>
                    {member && (
                      <div className="flex items-center gap-1 mt-1">
                        <div
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: member?.color }}
                        />
                        <span className="text-xs text-muted-foreground">
                          Added by {member?.name}
                        </span>
                      </div>
                    )}
                  </div>

                  <button
                    onClick={() => handleDeleteItem(item?.id)}
                    className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity p-2 hover:bg-destructive/10 rounded"
                    title="Delete item"
                  >
                    <Icon name="trash-2" size={16} className="text-destructive" />
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Add new item input */}
      <div className="p-6 border-t border-border bg-muted/30">
        <div className="flex gap-2">
          <input
            type="text"
            value={newItem}
            onChange={(e) => setNewItem(e?.target?.value)}
            onKeyDown={(e) => {
              if (e?.key === 'Enter') {
                handleAddItem();
              }
            }}
            placeholder="Add grocery item..."
            className="flex-1 px-4 py-3 border border-input rounded-md bg-background text-foreground text-base focus:outline-none focus:ring-2 focus:ring-primary"
          />
          <Button
            onClick={handleAddItem}
            disabled={!newItem?.trim()}
            iconName="plus"
            size="lg"
          >
            Add Item
          </Button>
        </div>
      </div>
    </div>
  );
};

export default GroceryListPanel;